import React, { useEffect, useState } from "react";
import {
    Container,
    Card,
    CardContent,
    Grid,
    Typography,
    TextField,
    Autocomplete,
    Box,
} from "@mui/material";

import {
    useGetHourlyActivitiesQuery,
    useGetHourlyLocationsQuery,
    useGetHourlyCourtsQuery,
    useFindHourlySlotsMutation,
} from "../../../store/hourlySlots/hourlySlotsApis";

import Breadcrumbs from "../../../components/layout/Breadcrumbs";
import Input from "../../../components/Common/Input";
import Button from "../../../components/Common/Button";


const HourlySlotsContainer = () => {
   
    const [date, setDate] = useState("");
    const [activity, setActivity] = useState(null);
    const [location, setLocation] = useState(null);
    const [court, setCourt] = useState(null);
    const [searchResults, setSearchResults] = useState([]);

    const {
        data: activities = [],
        isLoading: activitiesLoading,
        isError: activitiesError,
    } = useGetHourlyActivitiesQuery();

    const {
        data: locations = [],
        isLoading: locationsLoading,
        isError: locationsError,
    } = useGetHourlyLocationsQuery(
        activity?._id,
        {
            skip: !activity?._id,
        }
    );

    const {
        data: courts = [],
        isLoading: courtsLoading,
        isError: courtsError,
    } = useGetHourlyCourtsQuery(
        {
            activityId: activity?._id,
            locationId: location?._id,
        },
        {
            skip: !activity?._id || !location?._id,
        }
    );

    const [
        findHourlySlots,
        {
            isLoading: findSlotsLoading,
            isError: findSlotsError,
        },
    ] = useFindHourlySlotsMutation();

    const handleFind = async () => {
        if (!date || !activity?._id || !location?._id || !court?.court) {
            return;
        }

        try {
            const payload = {
                booking_date: date,
                activity_id: activity._id,
                location_id: location._id,
                court: court.court,
            };

            const result = await findHourlySlots(payload).unwrap();

            setSearchResults(result || []);
        } catch (error) {
            console.error("Failed to find hourly slots:", error);
            setSearchResults([]);
        }
    };

    const getEndTime = (time) => {
        if (!time) return "-";

        const match = time.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);

        if (!match) {
            return "-";
        }

        let hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        const period = match[3].toUpperCase();

        // Convert to 24-hour format
        if (period === "AM" && hours === 12) {
            hours = 0;
        }

        if (period === "PM" && hours !== 12) {
            hours += 12;
        }

        hours += 1;

        // Handle 12 PM -> 1 PM, 11 PM -> 12 AM
        if (hours >= 24) {
            hours -= 24;
        }

        const endPeriod = hours >= 12 ? "PM" : "AM";
        let displayHours = hours % 12;

        if (displayHours === 0) {
            displayHours = 12;
        }

        return `${String(displayHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${endPeriod}`;
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                py: 4,
            }}
        >
                <Card
                    sx={{
                        borderRadius: 3,
                        mb: 4,
                    }}
                >
                    <CardContent sx={{ p: 4 }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 500,
                                fontSize: "18px",
                                mt: 2,
                                mb: 3,
                            }}
                        >
                            Hourly Slots
                        </Typography>
                    </CardContent>
                </Card>

                <Card
                    sx={{
                        borderRadius: 3,
                        mb: 4,
                    }}
                >
                    <CardContent sx={{ p: 4 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                mb: 3,
                            }}
                        >
                            Search Filters
                        </Typography>

                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} md={3}>
                                <Input
                                    fullWidth
                                    label="Select Date"
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <Autocomplete
                                    options={activities}
                                    value={activity}
                                    loading={activitiesLoading}
                                    onChange={(event, value) => {
                                        setActivity(value);
                                        setLocation(null);
                                        setCourt(null);
                                    }}
                                    getOptionLabel={(option) => option.name || ""}
                                    isOptionEqualToValue={(option, value) =>
                                        option._id === value?._id
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Activity"
                                            error={activitiesError}
                                            helperText={
                                                activitiesError
                                                    ? "Failed to load activities"
                                                    : ""
                                            }
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <Autocomplete
                                    options={locations}
                                    value={location}
                                    loading={locationsLoading}
                                    disabled={!activity}
                                    onChange={(event, value) => {
                                        setLocation(value);
                                        setCourt(null);
                                    }}
                                    getOptionLabel={(option) => option.title || ""}
                                    isOptionEqualToValue={(option, value) =>
                                        option._id === value?._id
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Location"
                                            error={locationsError}
                                            helperText={
                                                locationsError
                                                    ? "Failed to load locations"
                                                    : ""
                                            }
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <Autocomplete
                                    options={courts}
                                    value={court}
                                    loading={courtsLoading}
                                    disabled={!location}
                                    onChange={(event, value) => {
                                        setCourt(value);
                                    }}
                                    getOptionLabel={(option) => option.court || ""}
                                    isOptionEqualToValue={(option, value) =>
                                        option.court === value?.court
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Court"
                                            error={courtsError}
                                            helperText={
                                                courtsError
                                                    ? "Failed to load courts"
                                                    : ""
                                            }
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleFind}
                                    disabled={
                                        !date ||
                                        !activity ||
                                        !location ||
                                        !court ||
                                        findSlotsLoading
                                    }
                                    sx={{
                                        height: 56,
                                        borderRadius: 2,
                                        fontWeight: 600
                                    }}
                                >
                                    {findSlotsLoading ? "Finding..." : "Find Slots"}
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Card
                    sx={{
                        borderRadius: 3,
                    }}
                >
                    <CardContent sx={{ p: 4 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                mb: 3,
                            }}
                        >
                            Search Results
                        </Typography>

                        {findSlotsError && (
                            <Box
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    borderRadius: 2,
                                    backgroundColor: "#fdecea",
                                }}
                            >
                                <Typography color="error">
                                    Failed to fetch hourly slots.
                                </Typography>
                            </Box>
                        )}

                        {searchResults.length === 0 && !findSlotsLoading ? (
                            <Box
                                sx={{
                                    p: 5,
                                    textAlign: "center",
                                    border: "1px dashed #ccc",
                                    borderRadius: 2,
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 600,
                                        mb: 1,
                                    }}
                                >
                                    No Booked or Pending Slots Found
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Select Date, Activity, Location and Court,
                                    then click "Find Slots".
                                </Typography>
                            </Box>
                        ) : (
                            <Box>
                                {searchResults.map((slot, index) => (
                                    <Box
                                        key={
                                            slot.booking_record_id ||
                                            slot.booking_id ||
                                            index
                                        }
                                        sx={{
                                            border: "1px solid #e0e0e0",
                                            borderRadius: 2,
                                            p: 3,
                                            mb: 2,
                                            backgroundColor: "#fff",
                                        }}
                                    >
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={3}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Slot Time
                                                </Typography>

                                                <Typography
                                                    variant="body1"
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    {slot.booking_time
                                                    ? `${slot.booking_time} - ${getEndTime(slot.booking_time)}`
                                                    : "-"}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12} md={2}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Status
                                                </Typography>

                                                <Typography
                                                    variant="body1"
                                                    sx={{ fontWeight: 700 }}
                                                >
                                                    {slot.status || "-"}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12} md={2}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Booking ID
                                                </Typography>

                                                <Typography
                                                    variant="body1"
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    {slot.booking_id || "-"}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12} md={3}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Booked By
                                                </Typography>

                                                <Typography
                                                    variant="body1"
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    {slot.booked_by?.name ||
                                                        slot.booked_by?.full_name ||
                                                        slot.booked_by?.member_name ||
                                                        "-"}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12} md={2}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Amount
                                                </Typography>

                                                <Typography
                                                    variant="body1"
                                                    sx={{ fontWeight: 700 }}
                                                >
                                                    ₹{slot.total_amount || 0}
                                                </Typography>
                                            </Grid>
                                        </Grid>

                                        {slot.players?.length > 0 && (
                                            <Box sx={{ mt: 3 }}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mb: 1,
                                                        fontWeight: 700, // Bold
                                                    }}
                                                >
                                                    Players
                                                </Typography>

                                                {slot.players.map((player, playerIndex) => (
                                                    <Typography
                                                        key={playerIndex}
                                                        variant="body2"
                                                        sx={{
                                                            mb: 1,
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {player.name ||
                                                            player.full_name ||
                                                            player.member_name ||
                                                            `Player ${playerIndex + 1}`}

                                                        {" - "}

                                                        {player.member_id
                                                            ? `Member ID: ${player.member_id}`
                                                            : "Non-Member"}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </CardContent>
                </Card>
        </Box>
    );
};

export default HourlySlotsContainer;